import { useEffect, useState } from "react";
import { Icon } from "@/components/Icons";
import type { ArchiveReason, FeedSignal, LifecycleStatus } from "../../types";
import { useAssignableUsers } from "../../hooks";
import type { SignalActionMutations } from "../../hooks";
import { abbreviateName, archiveReasonLabel, shortDate } from "../../format";
import { useRecipientMode } from "../../view-mode";
import { isClosedPursuit } from "../../stage";
import { RestoreControl } from "./RestoreControl";

/**
 * Zone 1 · header (sealed detail mock). Floating white glass: score + tier with
 * a breakdown tooltip, the mono company/verification eyebrow, the headline, the
 * ownership status line, the star, and the action row.
 *
 * Action row states (sealed):
 *   - Unclaimed: Claim is the lone hero; Assign / Share / Archive are quiet p3.
 *   - Claimed by the viewer: Claim collapses to an inert "Owned by you" chip;
 *     Reassign / Share / Archive p3; Unclaim is the quietest control and opens
 *     the release confirm.
 * Claimed-by-another and assigned states are not depicted in the mock; they
 * render honestly (Claim disabled, the status line names the owner).
 *
 * The whole action row + the star are hidden for a Magic Link recipient
 * (useRecipientMode), per the §M3 permission rules.
 */

const ARCHIVE_REASONS: ArchiveReason[] = [
  "no_strategic_fit",
  "bad_timing",
  "not_relevant",
  "already_engaged",
  "other",
];

const STATUS_LABEL: Partial<Record<LifecycleStatus, string>> = {
  won: "Won",
  passed: "Passed",
  expired: "Expired",
  suppressed: "Suppressed",
};

const VERIF_LABEL: Record<FeedSignal["verification"], string> = {
  verified: "Verified",
  quarantined: "Quarantined",
  unverified: "Unverified",
};

const STAR_PATH =
  "M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9z";

type Popover = "unclaim" | "assign" | "share" | "archive" | "reopen" | null;

export function ModalHeader({
  signal,
  viewerId,
  isAdmin,
  actions,
}: {
  signal: FeedSignal;
  viewerId: string;
  isAdmin: boolean;
  actions: SignalActionMutations;
}): JSX.Element {
  const recipient = useRecipientMode();
  const [open, setOpen] = useState<Popover>(null);

  useEffect(() => {
    if (!open) return;
    // Outside-press dismissal on a document capture-phase pointerdown. The safe
    // zone is each `.sx-action-item` (a trigger + its own popover), NOT the whole
    // row: the row's empty trailing space and inter-button gaps must dismiss too.
    const onDown = (e: PointerEvent) => {
      const target = e.target;
      if (!(target instanceof Element) || !target.closest(".sx-action-item")) {
        setOpen(null);
      }
    };
    // Layered Escape: one Escape closes the popover before the modal's own
    // Escape handler can close the whole modal. Capture phase wins regardless of
    // listener order.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const archived = signal.lifecycleStatus === "archived";
  const closed = isClosedPursuit(signal.lifecycleStatus);
  const isOwner = signal.claimedBy === viewerId || signal.assignedTo === viewerId;
  // R-2: reopen is a correction, and corrections are operator-only. The server
  // rejects a non-admin record_outcome(reopen) with a 403 regardless of
  // ownership, so this matches ModalRail's corrections gate rather than the
  // looser canUseAssignedSignal rule that governs forward actions.
  const canReopen = closed && isAdmin;
  const claimedByViewer =
    signal.ownershipState === "claimed" && signal.claimedBy === viewerId;
  const canClaim =
    !archived &&
    (signal.ownershipState === "unclaimed" ||
      (signal.ownershipState === "assigned" && signal.assignedTo === viewerId));
  const canAssign = isAdmin && !archived;
  const canShare = !archived;
  const canArchive = !archived && (isAdmin || isOwner);

  const statusLine = ownershipStatusLine(signal, viewerId);

  return (
    <header className="sx-z-header">
      <div className="sx-hd-top">
        <div className="sx-score">
          <button
            type="button"
            className="sx-score-trigger"
            aria-label={`Score ${signal.scfScore} of 100, Tier ${signal.tier}. Show breakdown.`}
          >
            <span className="sx-val">
              {signal.scfScore}
              <span className="sx-den">/100</span>
            </span>
            <span className="sx-tier" style={{ display: "block" }}>
              TIER {signal.tier}
            </span>
          </button>
          <div className="sx-score-tip" role="tooltip">
            <p className="sx-eyebrow" style={{ marginBottom: 5 }}>
              Score breakdown
            </p>
            <table>
              <tbody>
                <tr>
                  <td>Signal weight</td>
                  <td>{signal.scoreBreakdown.signalWeight}</td>
                </tr>
                <tr>
                  <td>Capability fit</td>
                  <td>{signal.scoreBreakdown.capabilityFit}</td>
                </tr>
                <tr>
                  <td>Recency</td>
                  <td>{signal.scoreBreakdown.recency}</td>
                </tr>
                <tr>
                  <td>Target account</td>
                  <td>{signal.scoreBreakdown.targetAccountBonus}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="sx-hd-main">
          <p className="sx-meta">
            {signal.company} · {VERIF_LABEL[signal.verification]}
          </p>
          <h1 className="sx-headline" id="sig-headline">
            {signal.headline}
          </h1>
          <p className="sx-meta" style={{ color: "var(--action-text)" }}>
            <span className="sx-dot" />
            {statusLine}
          </p>
          {/* R-4: Restore sits directly beneath the archive-state line — the
              ruled surface. Renders only for an archived signal whose viewer is
              the owner or an admin, and never in the recipient tier. */}
          {recipient ? null : (
            <RestoreControl
              signal={signal}
              viewerId={viewerId}
              isAdmin={isAdmin}
              actions={actions}
            />
          )}
        </div>

        {recipient ? null : (
          <button
            type="button"
            className={`sx-star-lg${signal.saved ? " sx-star--on" : ""}`}
            aria-label="Save this signal"
            aria-pressed={signal.saved}
            disabled={actions.toggleSave.isPending}
            onClick={() => {
              actions.toggleSave.reset();
              actions.toggleSave.mutate();
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={STAR_PATH} />
            </svg>
          </button>
        )}
      </div>

      {actions.toggleSave.isError ? (
        <p className="sx-inline-error" role="status">
          Save didn't stick. Try again.
        </p>
      ) : null}

      {recipient ? null : (
        <div className="sx-hd-actions">
          {closed ? (
            // Closed pursuit (Item 2): the Claim hero is replaced by a Reopen
            // affordance (outcome_reopened), with a confirm dialog like Won/Passed.
            <div className="sx-action-item">
              <button
                type="button"
                className="sx-btn sx-btn-hero"
                disabled={!canReopen}
                aria-haspopup="dialog"
                aria-expanded={open === "reopen"}
                title={
                  !canReopen
                    ? "Reopening is available to the owner or an admin"
                    : undefined
                }
                onClick={() => {
                  actions.recordOutcome.reset();
                  setOpen(open === "reopen" ? null : "reopen");
                }}
              >
                <Icon name="check" />
                Reopen pursuit
              </button>
              {open === "reopen" ? (
                <ReopenConfirm actions={actions} onDone={() => setOpen(null)} />
              ) : null}
            </div>
          ) : claimedByViewer ? (
            <span className="sx-owner-chip">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#08575E"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              <span className="sx-oc-main">Owned by you</span>
            </span>
          ) : (
            <div className="sx-action-item">
              <button
                type="button"
                className="sx-btn sx-btn-hero"
                disabled={!canClaim || actions.claim.isPending}
                onClick={() => {
                  actions.claim.reset();
                  actions.claim.mutate();
                }}
              >
                <Icon name="check" />
                {actions.claim.isPending ? "Claiming…" : "Claim this signal"}
              </button>
              {actions.claim.isError ? (
                <div className="sx-popover">
                  <p className="sx-popover-help sx-popover-help--error">
                    {claimErrorMessage(actions.claim.error)}
                  </p>
                </div>
              ) : null}
            </div>
          )}

          <div className="sx-action-item">
            <button
              type="button"
              className="sx-btn sx-btn-p3"
              disabled={!canAssign}
              aria-haspopup="dialog"
              aria-expanded={open === "assign"}
              onClick={() => setOpen(open === "assign" ? null : "assign")}
            >
              {claimedByViewer ? "Reassign" : "Assign"}
            </button>
            {open === "assign" ? (
              <AssignPopover actions={actions} onDone={() => setOpen(null)} />
            ) : null}
          </div>

          {isAdmin ? (
            <div className="sx-action-item">
              <button
                type="button"
                className="sx-btn sx-btn-p3"
                disabled={!canShare}
                aria-haspopup="dialog"
                aria-expanded={open === "share"}
                onClick={() => {
                  actions.share.reset();
                  setOpen(open === "share" ? null : "share");
                }}
              >
                Share
              </button>
              {open === "share" ? (
                <SharePopover actions={actions} onDone={() => setOpen(null)} />
              ) : null}
            </div>
          ) : null}

          <div className="sx-action-item">
            <button
              type="button"
              className="sx-btn sx-btn-p3"
              disabled={!canArchive}
              aria-haspopup="dialog"
              aria-expanded={open === "archive"}
              title={
                !canArchive && !archived
                  ? "Archiving is available to the assigned or claiming user"
                  : undefined
              }
              onClick={() => setOpen(open === "archive" ? null : "archive")}
            >
              Archive
            </button>
            {open === "archive" ? (
              <ArchiveMenu actions={actions} onDone={() => setOpen(null)} />
            ) : null}
          </div>

          {claimedByViewer && !closed ? (
            <div className="sx-action-item">
              <button
                type="button"
                className="sx-btn sx-btn-p3 sx-btn-quiet"
                aria-haspopup="dialog"
                aria-expanded={open === "unclaim"}
                disabled={actions.unclaim.isPending}
                onClick={() => setOpen(open === "unclaim" ? null : "unclaim")}
              >
                Unclaim
              </button>
              {open === "unclaim" ? (
                <UnclaimConfirm actions={actions} onDone={() => setOpen(null)} />
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </header>
  );
}

/** Uppercased status line: owner + updated + first seen. CSS uppercases it. */
function ownershipStatusLine(signal: FeedSignal, viewerId: string): string {
  const updated = signal.lastObservedAt
    ? ` · Updated ${shortDate(signal.lastObservedAt)}`
    : "";
  const firstSeen = signal.firstObservedAt
    ? ` · First seen ${shortDate(signal.firstObservedAt)}`
    : "";

  let owner: string;
  if (signal.lifecycleStatus === "archived") {
    owner = signal.archiveReason
      ? `Archived: ${archiveReasonLabel(signal.archiveReason)}`
      : "Archived";
  } else if (STATUS_LABEL[signal.lifecycleStatus]) {
    owner = STATUS_LABEL[signal.lifecycleStatus] as string;
  } else if (signal.ownershipState === "unclaimed") {
    owner = "Unclaimed";
  } else if (signal.ownershipState === "claimed") {
    owner =
      signal.claimedBy === viewerId
        ? "Owned by you"
        : `Claimed by ${abbreviateName(signal.claimedByName)}`;
  } else {
    owner =
      signal.assignedTo === viewerId
        ? "Assigned to you"
        : `Assigned to ${abbreviateName(signal.assignedToName)}`;
  }
  return `${owner}${updated}${firstSeen}`;
}

/* M5 · AssignPopover — type-ahead teammate search. */
function AssignPopover({
  actions,
  onDone,
}: {
  actions: SignalActionMutations;
  onDone: () => void;
}): JSX.Element {
  const { data: assignees = [], isPending: loadingUsers } = useAssignableUsers();
  const [query, setQuery] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [note, setNote] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = q
    ? assignees.filter((a) => assignableSearchText(a).includes(q))
    : assignees;
  const selected = assignees.find((a) => a.id === assigneeId);
  const name = selected?.name.split(" ")[0] ?? "the assignee";
  const result = actions.assign.data;

  if (result) {
    const delivery =
      result.notificationStatus === "sent"
        ? `Assignment email sent to ${result.assigneeEmail}.`
        : result.notificationStatus === "not_configured"
          ? "Assignment recorded. Email delivery is not configured yet."
          : "Assignment recorded, but the email could not be sent.";
    return (
      <div className="sx-popover" role="dialog" aria-label="Assignment recorded">
        <div className="sx-popover-title">Assigned to {result.assigneeName}</div>
        <p className="sx-popover-help" role="status">{delivery}</p>
        <button type="button" className="sx-cta" onClick={onDone}>Done</button>
      </div>
    );
  }

  return (
    <div className="sx-popover">
      <div className="sx-popover-title">Assign to</div>
      <input
        className="sx-field"
        value={query}
        autoFocus
        placeholder="Search by name, email, title, or practice…"
        aria-label="Search teammates"
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="sx-assign-list" role="listbox" aria-label="Teammates">
        {loadingUsers ? (
          <div className="sx-assign-empty">Loading teammates…</div>
        ) : filtered.length === 0 ? (
          <div className="sx-assign-empty">
            {assignees.length === 0
              ? "No teammates available"
              : `No teammates match "${query}"`}
          </div>
        ) : (
          filtered.map((a) => (
            <button
              key={a.id}
              type="button"
              role="option"
              aria-selected={a.id === assigneeId}
              className={`sx-assign-option${a.id === assigneeId ? " sx-assign-option--selected" : ""}`}
              onClick={() => setAssigneeId(a.id)}
            >
              <span className="sx-assign-avatar" aria-hidden="true">
                {initials(a.name)}
              </span>
              <span className="sx-assign-name">{a.name}</span>
              <span className="sx-assign-meta">
                {[a.title, a.practice, a.email].filter(Boolean).join(" · ")}
              </span>
              {a.id === assigneeId ? <Icon name="check" /> : null}
            </button>
          ))
        )}
      </div>
      {selected ? (
        <>
          <div className="sx-popover-label">Context for {name}</div>
          <textarea
            className="sx-field"
            value={note}
            placeholder="What should they know?"
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="sx-popover-help">
            Shared with the assignee and recorded in the signal history
          </p>
        </>
      ) : null}
      <button
        type="button"
        className="sx-cta"
        disabled={actions.assign.isPending || !assigneeId}
        onClick={() =>
          assigneeId &&
          actions.assign.mutate(
            { assigneeId, note: note.trim() || undefined },
          )
        }
      >
        {selected ? `Assign to ${name}` : "Assign"}
      </button>
    </div>
  );
}

function assignableSearchText(assignee: {
  name: string;
  email: string;
  practice: string | null;
  title: string | null;
}): string {
  return [assignee.name, assignee.email, assignee.practice, assignee.title]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function SharePopover({
  actions,
  onDone,
}: {
  actions: SignalActionMutations;
  onDone: () => void;
}): JSX.Element {
  const { data: recipients = [], isPending: loadingUsers } = useAssignableUsers();
  const [query, setQuery] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [copied, setCopied] = useState(false);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? recipients.filter((candidate) => assignableSearchText(candidate).includes(q))
    : recipients;
  const url = actions.share.data?.url;

  if (url) {
    return (
      <div className="sx-popover sx-share-popover" role="dialog" aria-label="Magic Link created">
        <div className="sx-popover-title">Magic Link created</div>
        <p className="sx-popover-help">Copy this secure link into your email.</p>
        <input className="sx-field" readOnly value={url} aria-label="Created Magic Link" />
        <div className="sx-popover-actions">
          <button type="button" className="sx-btn sx-btn-p3" onClick={onDone}>Done</button>
          <button
            type="button"
            className="sx-cta"
            onClick={() => {
              void navigator.clipboard.writeText(url);
              setCopied(true);
            }}
          >
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sx-popover sx-share-popover" role="dialog" aria-label="Create Magic Link">
      <div className="sx-popover-title">Create Magic Link</div>
      <input
        className="sx-field"
        value={query}
        autoFocus
        placeholder="Search by name, email, title, or practice…"
        aria-label="Search Magic Link recipients"
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="sx-assign-list" role="listbox" aria-label="Magic Link recipients">
        {loadingUsers ? (
          <div className="sx-assign-empty">Loading teammates…</div>
        ) : filtered.length === 0 ? (
          <div className="sx-assign-empty">No teammates match “{query}”</div>
        ) : (
          filtered.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              role="option"
              aria-selected={candidate.id === recipientId}
              className={`sx-assign-option${candidate.id === recipientId ? " sx-assign-option--selected" : ""}`}
              onClick={() => setRecipientId(candidate.id)}
            >
              <span className="sx-assign-avatar" aria-hidden="true">{initials(candidate.name)}</span>
              <span className="sx-assign-name">{candidate.name}</span>
              <span className="sx-assign-meta">
                {[candidate.title, candidate.practice, candidate.email].filter(Boolean).join(" · ")}
              </span>
              {candidate.id === recipientId ? <Icon name="check" /> : null}
            </button>
          ))
        )}
      </div>
      {actions.share.isError ? (
        <p className="sx-popover-help sx-popover-help--error">Couldn’t create the link. Try again.</p>
      ) : null}
      <button
        type="button"
        className="sx-cta"
        disabled={!recipientId || actions.share.isPending}
        onClick={() => recipientId && actions.share.mutate(recipientId)}
      >
        {actions.share.isPending ? "Creating…" : "Create Magic Link"}
      </button>
    </div>
  );
}

/* M4 · ArchiveReasonMenu */
function ArchiveMenu({
  actions,
  onDone,
}: {
  actions: SignalActionMutations;
  onDone: () => void;
}): JSX.Element {
  const [reason, setReason] = useState<ArchiveReason | null>(null);
  return (
    <div className="sx-popover sx-popover--end">
      <div className="sx-popover-title">Archive this signal</div>
      {ARCHIVE_REASONS.map((r) => (
        <button
          key={r}
          type="button"
          className="sx-menu-item"
          aria-pressed={reason === r}
          onClick={() => setReason(r)}
        >
          {archiveReasonLabel(r)}
        </button>
      ))}
      <button
        type="button"
        className="sx-cta"
        disabled={!reason || actions.archive.isPending}
        onClick={() => {
          if (reason) actions.archive.mutate(reason, { onSuccess: onDone });
        }}
      >
        Archive
      </button>
    </div>
  );
}

/* Reopen confirm (Item 2) — the confirmation step behind the Reopen hero on a
   closed pursuit. Records outcome_reopened, returning the pursuit to active. */
function ReopenConfirm({
  actions,
  onDone,
}: {
  actions: SignalActionMutations;
  onDone: () => void;
}): JSX.Element {
  const recordOutcome = actions.recordOutcome;
  return (
    <div className="sx-popover" role="dialog" aria-label="Confirm reopen pursuit">
      <div className="sx-popover-title">Reopen this pursuit?</div>
      <p className="sx-popover-help">
        Reopening returns the pursuit to active and clears the outcome. The closed
        record stays in the signal history.
      </p>
      {recordOutcome.isError ? (
        <p className="sx-popover-help sx-popover-help--error">
          Couldn&rsquo;t reopen — try again.
        </p>
      ) : null}
      <div className="sx-popover-actions">
        <button type="button" className="sx-btn sx-btn-p3" onClick={onDone}>
          Cancel
        </button>
        <button
          type="button"
          className="sx-cta"
          disabled={recordOutcome.isPending}
          onClick={() =>
            recordOutcome.mutate({ outcome: "reopen" }, { onSuccess: onDone })
          }
        >
          {recordOutcome.isPending ? "Reopening…" : "Reopen"}
        </button>
      </div>
    </div>
  );
}

/* Unclaim confirm — the release step behind the quiet Unclaim button. */
function UnclaimConfirm({
  actions,
  onDone,
}: {
  actions: SignalActionMutations;
  onDone: () => void;
}): JSX.Element {
  return (
    <div className="sx-popover sx-popover--end">
      <div className="sx-popover-title">Release this claim?</div>
      <p className="sx-popover-help">
        Returns the signal to Unclaimed and records an unclaim event. Nothing is
        archived or reassigned.
      </p>
      {actions.unclaim.isError ? (
        <p className="sx-popover-help sx-popover-help--error">
          Couldn&rsquo;t release the claim. Try again.
        </p>
      ) : null}
      <div className="sx-popover-actions">
        <button type="button" className="sx-btn sx-btn-p3" onClick={onDone}>
          Keep claim
        </button>
        <button
          type="button"
          className="sx-cta"
          disabled={actions.unclaim.isPending}
          onClick={() => actions.unclaim.mutate(undefined, { onSuccess: onDone })}
        >
          {actions.unclaim.isPending ? "Releasing…" : "Unclaim"}
        </button>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

function claimErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/claim|assigned|ownership|forbidden|403|cannot/i.test(message)) {
    return "Claim did not complete. This signal may have been claimed or assigned.";
  }
  return "Claim did not complete. Try again.";
}
